<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Swap Details Box -->
            <div class="border border-dark p-4 mb-4 text-sm rounded" v-if="displaySwapDetails">
              <div class="mb-2">
                <strong>LOCATION:</strong> {{ displaySwapDetails.location }}
              </div>
              <div class="mb-2">
                <strong>YOUR EMAIL:</strong> {{ displaySwapDetails.email }}
              </div>
              <div class="mb-0">
                <strong>SWAP DESCRIPTION:</strong> {{ displaySwapDetails.swapDescription }}
              </div>
            </div>

            <!-- User Greeting -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
            </div>

            <!-- Confirmation Question -->
            <div class="mb-4">
              <p class="text-danger fw-bold mb-3" style="font-size: 1.1rem;">
                Are you sure you want to decline this swap request?
              </p>
              
              <!-- Confirm Decline Button -->
              <div class="mb-4">
                <MaterialButton 
                  color="primary"
                  :disabled="declining"
                  @click="confirmDecline"
                >
                  {{ declining ? 'Processing...' : 'Click here to confirm decline' }}
                </MaterialButton>
              </div>
            </div>

            <!-- Divider -->
            <hr class="my-4" />

            <!-- Did You Know Section -->
            <div class="mb-4" v-if="showDidYouKnow">
              <h5 class="fw-bold mb-3">Did You Know?</h5>
              <p class="mb-3">{{ displayDidYouKnowText }}</p>
              <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
              </ul>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton color="success">
                Click here to Login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const declining = ref(false)
const swapDetails = ref({
  location: '',
  email: '',
  swapDescription: ''
})
const userName = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])

// Mock fallback data
const mockSwapDetails = {
  location: 'CS Test Church -> Chicago main Branch',
  email: 'Barbara.manchas@noemail.churchscheduling.com',
  swapDescription: 'Konada A (Konada_A_002@noemail.churchscheduling.com) requested to swap current assignment [Usher on Dec 28, 2015 9:00:00 AM with Barbara Manchas@noemail.churchscheduling.com] for [Usher on Jan 11, 2020 10:00:00 AM]'
}
const mockUserName = 'Barbara'
const mockDidYouKnowText = 'You can request to swap an assignment by using the steps below:'
const mockDidYouKnowSteps = [
  "log into the application and click on the 'My Assignments' button from the Home page",
  "Click on the 'Request Swap' button next to the assignment record you want to swap",
  "Click on the button of the desired assignment date with the name of the person you want to swap with"
]

// Computed display values (prefer API, fallback to mock)
const displaySwapDetails = computed(() =>
  swapDetails.value && Object.values(swapDetails.value).some(v => v)
    ? swapDetails.value
    : mockSwapDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() =>
  (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps
)

// Fetch page details on mount
const fetchSwapRequestDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/swapemailrequest-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.assignmentDetails) {
        swapDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          swapDescription: data.assignmentDetails.swap_description || ''
        }
      }

      userName.value = data.username || ''
      if (data.showDidYouKnow != null) showDidYouKnow.value = data.showDidYouKnow
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
    }
  } catch (error) {
    console.error('Error fetching swap request details:', error)
  } finally {
    loading.value = false
  }
}

// Confirm decline action
const confirmDecline = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString || declining.value) return

  declining.value = true
  try {
    const response = await api.post(`/account/swapemailrequest-decline/cs/${encodedString}`)
    const msg = response.data?.message || 'Swap request declined successfully.'
    toast.success(msg, { position: 'top-right' })
  } catch (error) {
    console.error('Error declining swap request:', error)
    const errMsg = error.response?.data?.message || error.message || 'Failed to decline swap request.'
    toast.error(errMsg, { position: 'top-right' })
  } finally {
    declining.value = false
  }
}

onMounted(async () => {
  await fetchSwapRequestDetails()
})
</script>

